import { type ArdoqId } from '@ardoq/api-types';
import { componentUtils } from '@ardoq/scope-data';
import { type EnhancedScopeData } from '@ardoq/data-model';
import { RawGraphNode2 } from '@ardoq/graph';
import { uniq } from 'lodash';

type BuildComponentItemsArgs = {
  scopeData: EnhancedScopeData;
  componentMap: Map<ArdoqId, unknown>;
  groupMap: Map<string, RawGraphNode2>;
  degreesOfChildhood: number;
  degreesOfParenthood: number;
  withRelatedComponents: (
    componentIds: ArdoqId[],
    degreesOfChildhood: number,
    degreesOfParenthood: number
  ) => ArdoqId[];
};

export const buildComponentItems = ({
  scopeData,
  componentMap,
  groupMap,
  degreesOfChildhood,
  degreesOfParenthood,
  withRelatedComponents,
}: BuildComponentItemsArgs): Map<string, RawGraphNode2> => {
  const componentGroupIds = Array.from(groupMap.values())
    .map(group => group.modelId)
    .filter(
      maybeComponentId =>
        maybeComponentId &&
        componentUtils.isComponent(scopeData, maybeComponentId)
    ) as ArdoqId[];

  // componentMap intentionally does not contain reference parents and children
  const allComponentIds = uniq(
    Array.from(componentMap.keys()).concat(componentGroupIds)
  );

  const withPotentialReferencedComponentsHierarchy = withRelatedComponents(
    allComponentIds,
    degreesOfChildhood,
    degreesOfParenthood
  );

  /**
   * Component nodes query data by the modelId, unless they are a group node.
   * In that case, the index is the item id, which is a concatenated path.
   */
  return new Map<string, RawGraphNode2>(
    withPotentialReferencedComponentsHierarchy.map(id => [
      id,
      {
        id,
        modelId: id,
        label: '',
        subLabels: [],
      },
    ])
  );
};
